import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../config/api';
import Swal from 'sweetalert2';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // Start as false - show page immediately
  const [loadingUsers, setLoadingUsers] = useState(false); // Separate loading state for users table
  const [stats, setStats] = useState({
    total_orgs: 0,
    total_users: 0,
    total_qurators: 0,
    total_qures: 0
  });
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filters, setFilters] = useState({
    user_type: '',
    org_code: '',
    search: ''
  });

  // Modals
  const [showOrgListModal, setShowOrgListModal] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Form data
  const [orgForm, setOrgForm] = useState({
    org_id: '',
    org_name: '',
    org_code: '',
    number_of_qurator: 5,
    number_of_qure: 20
  });
  const [userForm, setUserForm] = useState({
    user_id: '',
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    user_type: 'qurator',
    user_code: '',
    pin: '',
    password: '',
    auto_generate_password: true
  });
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Check user and redirect if not admin (matching PHP behavior)
    if (!user) {
      // Wait for user to load from context
      return;
    }
    
    const userId = user.user_id;
    if (userId !== 79 && userId !== '79' && parseInt(userId) !== 79) {
      Swal.fire('Access Denied', 'You are not authorized to access this page.', 'error').then(() => {
        navigate('/qurator-dashboard');
      });
      return;
    }
    
    // Load data immediately (matching PHP DOMContentLoaded behavior)
    // Don't await - let them load in parallel
    loadStats().catch(err => console.error('Stats load error:', err));
    loadOrganizations().catch(err => console.error('Organizations load error:', err));
    loadUsers(1).catch(err => console.error('Users load error:', err));
  }, [user, navigate]);

  // Watch for filter changes and reload users (except on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (user && (user.user_id === 79 || user.user_id === '79' || parseInt(user.user_id) === 79)) {
      loadUsers(1).catch(err => console.error('Filter change error:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.user_type, filters.org_code]); // Only watch these two, search is handled separately

  const loadStats = async () => {
    try {
      const response = await API.get('/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      } else {
        console.error('Load stats failed:', response.data);
      }
    } catch (error) {
      console.error('Load stats error:', error);
      console.error('Error response:', error.response?.data);
      // Don't show alert on initial load, just log
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await API.get('/admin/organizations');
      if (response.data.success) {
        setOrganizations(response.data.organizations || []);
      } else {
        console.error('Load organizations failed:', response.data);
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Load organizations error:', error);
      console.error('Error response:', error.response?.data);
      setOrganizations([]);
      // Don't show alert on initial load, just log
    }
  };

  const loadAllOrganizations = async () => {
    try {
      const response = await API.get('/admin/organizations/all');
      if (response.data.success) {
        setAllOrganizations(response.data.organizations);
        setShowOrgListModal(true);
      }
    } catch (error) {
      console.error('Load all organizations error:', error);
    }
  };

  const loadUsers = async (page = 1) => {
    setLoadingUsers(true); // Show loading (set OUTSIDE try so it always runs first)
    try {
      const params = { page };
      // Only add non-empty filters (matching PHP behavior)
      if (filters.user_type) params.user_type = filters.user_type;
      if (filters.org_code) params.org_code = filters.org_code;
      if (filters.search) params.search = filters.search;
      
      const response = await API.get('/admin/users', { params });
      
      if (response.data.success) {
        setUsers(response.data.users || []);
        setTotalUsers(response.data.total || 0);
        setCurrentPage(page);
      } else {
        console.error('Load users API error:', response.data.message);
        setUsers([]);
        setTotalUsers(0);
        Swal.fire('Error', response.data.message || 'Failed to load users', 'error');
      }
    } catch (error) {
      console.error('Load users error:', error.response?.data || error.message);
      setUsers([]);
      setTotalUsers(0);
      const msg = error.response?.data?.message || error.message || 'Failed to load users';
      Swal.fire('Error', msg, 'error');
    }
    setLoadingUsers(false); // Always called — outside try/catch so nothing can block it
  };

  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    try {
      if (orgForm.org_id) {
        // Update
        const response = await API.put(`/admin/organizations/${orgForm.org_id}`, {
          org_name: orgForm.org_name,
          number_of_qurator: orgForm.number_of_qurator,
          number_of_qure: orgForm.number_of_qure
        });
        if (response.data.success) {
          Swal.fire('Success', response.data.message, 'success');
          setShowEditOrgModal(false);
          loadStats();
          loadOrganizations();
          loadUsers(1);
        }
      } else {
        // Create
        const response = await API.post('/admin/organizations', orgForm);
        if (response.data.success) {
          Swal.fire('Success', response.data.message, 'success');
          setShowCreateOrgModal(false);
          resetOrgForm();
          loadStats();
          loadOrganizations();
          loadUsers(1);
        }
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (userForm.user_id) {
        // Update
        const response = await API.put(`/admin/users/${userForm.user_id}`, {
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          username: userForm.username,
          email: userForm.email,
          user_type: userForm.user_type,
          pin: userForm.pin,
          password: userForm.password || undefined
        });
        if (response.data.success) {
          Swal.fire('Success', response.data.message, 'success');
          setShowEditUserModal(false);
          resetUserForm();
          loadUsers(currentPage);
        }
      } else {
        // Create
        const response = await API.post('/admin/users', {
          ...userForm,
          password: userForm.auto_generate_password ? undefined : userForm.password
        });
        if (response.data.success) {
          if (response.data.password) {
            setGeneratedPassword(response.data.password);
            Swal.fire({
              title: 'User Created!',
              html: `<strong>Generated Password:</strong><br><code style="font-size: 18px; padding: 10px; background: #f0f0f0; display: inline-block; margin: 10px 0;">${response.data.password}</code><br><small>Save this password - it won't be shown again!</small>`,
              icon: 'success',
              confirmButtonText: 'OK'
            });
          } else {
            Swal.fire('Success', response.data.message, 'success');
          }
          setShowCreateUserModal(false);
          resetUserForm();
          loadUsers(1);
          loadOrganizations();
        }
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const toggleOrgStatus = async (orgId, status) => {
    try {
      const response = await API.patch(`/admin/organizations/${orgId}/status`, { status });
      if (response.data.success) {
        Swal.fire('Success', response.data.message, 'success');
        loadStats();
        loadOrganizations();
        loadUsers(1);
        if (showOrgListModal) {
          loadAllOrganizations();
        }
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const deleteOrganization = async (orgId) => {
    try {
      const response = await API.delete(`/admin/organizations/${orgId}`);
      if (response.data.success) {
        Swal.fire('Success', response.data.message, 'success');
        loadStats();
        loadOrganizations();
        loadUsers(1);
        if (showOrgListModal) {
          loadAllOrganizations();
        }
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const toggleUserStatus = async (userId, status) => {
    try {
      const response = await API.patch(`/admin/users/${userId}/status`, { status });
      if (response.data.success) {
        Swal.fire('Success', response.data.message, 'success');
        loadUsers(currentPage);
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await API.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        Swal.fire('Success', response.data.message, 'success');
        loadUsers(currentPage);
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const editOrganization = async (orgId) => {
    try {
      const response = await API.get(`/admin/organizations/${orgId}`);
      if (response.data.success) {
        setOrgForm({
          org_id: response.data.organization.org_id,
          org_name: response.data.organization.org_name,
          org_code: response.data.organization.org_code,
          number_of_qurator: response.data.organization.number_of_qurator,
          number_of_qure: response.data.organization.number_of_qure
        });
        setShowEditOrgModal(true);
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to load organization', 'error');
    }
  };

  const editUser = async (userId) => {
    try {
      const response = await API.get(`/admin/users/${userId}`);
      if (response.data.success) {
        const u = response.data.user;
        setUserForm({
          user_id: u.user_id,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          username: u.username || '',
          email: u.email || '',
          user_type: u.user_type || 'qurator',
          user_code: u.user_code || '',
          pin: u.pin || '',
          password: '',
          auto_generate_password: false
        });
        setShowEditUserModal(true);
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to load user', 'error');
    }
  };

  const resetOrgForm = () => {
    setOrgForm({
      org_id: '',
      org_name: '',
      org_code: '',
      number_of_qurator: 5,
      number_of_qure: 20
    });
  };

  const resetUserForm = () => {
    setUserForm({
      user_id: '',
      first_name: '',
      last_name: '',
      username: '',
      email: '',
      user_type: 'qurator',
      user_code: '',
      pin: '',
      password: '',
      auto_generate_password: true
    });
    setGeneratedPassword('');
  };

  const showConfirm = (action, data, message, type = 'warning', showWarning = false) => {
    setConfirmAction({ action, data, message, type, showWarning });
    setShowConfirmModal(true);
  };

  const executeConfirm = async () => {
    if (!confirmAction) return;

    try {
      let response;
      switch (confirmAction.action) {
        case 'toggle_org_status':
          response = await API.patch(`/admin/organizations/${confirmAction.data.org_id}/status`, {
            status: confirmAction.data.status
          });
          break;
        case 'delete_org':
          response = await API.delete(`/admin/organizations/${confirmAction.data.org_id}`);
          break;
        case 'toggle_user_status':
          response = await API.patch(`/admin/users/${confirmAction.data.user_id}/status`, {
            status: confirmAction.data.status
          });
          break;
        case 'delete_user':
          response = await API.delete(`/admin/users/${confirmAction.data.user_id}`);
          break;
        case 'bulk_deactivate_org':
          response = await API.post('/admin/users/bulk/deactivate-org', {
            org_code: confirmAction.data.org_code
          });
          break;
        case 'bulk_activate_org':
          response = await API.post('/admin/users/bulk/activate-org', {
            org_code: confirmAction.data.org_code
          });
          break;
        default:
          return;
      }

      if (response.data.success) {
        Swal.fire('Success', response.data.message, 'success');
        loadStats();
        loadOrganizations();
        loadUsers(1);
        if (showOrgListModal) {
          loadAllOrganizations();
        }
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
    } finally {
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };

  const bulkAction = (actionType) => {
    const orgCode = document.getElementById('bulkOrgSelect')?.value;
    if (!orgCode && (actionType === 'deactivate_org' || actionType === 'activate_org')) {
      Swal.fire('Warning', 'Please select an organization', 'warning');
      return;
    }

    const orgName = document.querySelector(`#bulkOrgSelect option[value="${orgCode}"]`)?.textContent || '';
    let message, action, data;

    switch (actionType) {
      case 'deactivate_org':
        message = `Deactivate all users in organization "${orgName}"?`;
        action = 'bulk_deactivate_org';
        data = { org_code: orgCode };
        break;
      case 'activate_org':
        message = `Activate all users in organization "${orgName}"?`;
        action = 'bulk_activate_org';
        data = { org_code: orgCode };
        break;
      default:
        return;
    }

    showConfirm(action, data, message, 'warning', false);
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };

  const copyUserDetails = async (user) => {
    const details = `Username: ${user.username || 'N/A'}
Password: ${user.plain_password || 'N/A'}
Org Code: ${user.user_code || 'N/A'}
PIN: ${user.pin || 'N/A'}`;

    try {
      await navigator.clipboard.writeText(details);
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'User details copied to clipboard',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = details;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        Swal.fire({
          icon: 'success',
          title: 'Copied!',
          text: 'User details copied to clipboard',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      } catch (err) {
        Swal.fire('Error', 'Failed to copy to clipboard', 'error');
      }
      document.body.removeChild(textArea);
    }
  };

  // Don't block on loading - show page immediately (matching PHP behavior)
  if (!user) {
    return (
      <div className="admin-dashboard-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading admin dashboard...</p>
      </div>
    );
  }

  if (user.user_id !== 79 && user.user_id !== '79' && parseInt(user.user_id) !== 79) {
    return null;
  }

  const totalPages = Math.ceil(totalUsers / 15);

  return (
    <div className="admin-dashboard">

      {/* ── TOP BAR ── */}
      <div className="adm-topbar">
        <div className="adm-topbar-left">
          <div className="adm-brand">
            <div className="adm-brand-icon"><i className="fas fa-crown"></i></div>
            <div>
              <div className="adm-brand-title">Admin Dashboard</div>
              <div className="adm-brand-subtitle">Organization & User Management</div>
            </div>
          </div>
        </div>
        <div className="adm-topbar-right">
          <span className="adm-welcome-text">Welcome, <strong>{user?.username || 'Admin'}</strong></span>
          <button className="adm-topbar-btn adm-topbar-btn-home tooltip-below" data-tooltip="Go to Home" onClick={() => navigate('/qurator-dashboard')}>
            <i className="fas fa-home"></i><span>Home</span>
          </button>
          <button className="adm-topbar-btn adm-topbar-btn-logout tooltip-below" data-tooltip="Sign Out" onClick={() => navigate('/login')}>
            <i className="fas fa-sign-out-alt"></i><span>Logout</span>
          </button>
        </div>
      </div>

      {/* ── PAGE BODY ── */}
      <div className="adm-body">

        {/* Stats Row */}
        <div className="adm-stats-row">
          <div className="adm-stat adm-stat-1">
            <div className="adm-stat-icon"><i className="fas fa-building"></i></div>
            <div>
              <div className="adm-stat-num">{stats.total_orgs}</div>
              <div className="adm-stat-label">Organizations</div>
            </div>
          </div>
          <div className="adm-stat adm-stat-2">
            <div className="adm-stat-icon"><i className="fas fa-users"></i></div>
            <div>
              <div className="adm-stat-num">{stats.total_users}</div>
              <div className="adm-stat-label">Total Users</div>
            </div>
          </div>
          <div className="adm-stat adm-stat-3">
            <div className="adm-stat-icon"><i className="fas fa-user-tie"></i></div>
            <div>
              <div className="adm-stat-num">{stats.total_qurators}</div>
              <div className="adm-stat-label">Qurators</div>
            </div>
          </div>
          <div className="adm-stat adm-stat-4">
            <div className="adm-stat-icon"><i className="fas fa-user"></i></div>
            <div>
              <div className="adm-stat-num">{stats.total_qures}</div>
              <div className="adm-stat-label">Qures</div>
            </div>
          </div>
        </div>

        {/* Organization Management Card */}
        <div className="adm-card">
          <div className="adm-card-header">
            <div className="adm-card-title"><i className="fas fa-building"></i> Organization Management</div>
            <div className="adm-section-actions">
              <button className="adm-btn adm-btn-white" data-tooltip="View and manage all organizations" onClick={loadAllOrganizations}>
                <i className="fas fa-list"></i> Manage All
              </button>
              <button className="adm-btn adm-btn-primary" data-tooltip="Create a new organization" onClick={() => { resetOrgForm(); setShowCreateOrgModal(true); }}>
                <i className="fas fa-plus"></i> Create Organization
              </button>
            </div>
          </div>
          <div className="adm-card-body">
            <div className="adm-org-browse">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <select
                    className="form-select"
                    value={filters.org_code}
                    onChange={(e) => {
                      setFilters({ ...filters, org_code: e.target.value });
                      setSelectedOrgId(organizations.find(org => org.org_code === e.target.value)?.org_id || null);
                    }}
                  >
                    <option value="">Select organization to view users...</option>
                    {organizations.map(org => (
                      <option key={org.org_id} value={org.org_code}>{org.org_name} ({org.org_code})</option>
                    ))}
                  </select>
                </div>
                <button className="adm-btn adm-btn-info" data-tooltip="Refresh all data" onClick={() => { loadStats(); loadOrganizations(); loadUsers(1); }}>
                  <i className="fas fa-sync-alt"></i> Refresh
                </button>
                <button className="adm-btn adm-btn-success" data-tooltip="Create a new user" onClick={() => { resetUserForm(); setShowCreateUserModal(true); }}>
                  <i className="fas fa-user-plus"></i> Add User
                </button>
                {selectedOrgId && (
                  <button className="adm-btn adm-btn-warning" data-tooltip="Edit selected organization" onClick={() => editOrganization(selectedOrgId)}>
                    <i className="fas fa-pen"></i> Edit Org
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* User Management Card */}
        <div className="adm-card">
          <div className="adm-card-header">
            <div className="adm-card-title"><i className="fas fa-users"></i> User Management</div>
            <div className="adm-search-group">
              <input
                type="text"
                placeholder="Search name, username, email..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({ ...filters, search: e.target.value });
                  if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                  searchTimeoutRef.current = setTimeout(() => loadUsers(1), 500);
                }}
              />
              <button data-tooltip="Search" onClick={() => loadUsers(1)}><i className="fas fa-search"></i></button>
            </div>
          </div>
          <div className="adm-card-body">

            {/* Filters */}
            <div className="adm-filters">
              <label><i className="fas fa-filter" style={{ color: '#FF5722' }}></i> Filters:</label>
              <select
                className="form-select"
                style={{ maxWidth: '180px' }}
                value={filters.user_type}
                onChange={(e) => { setFilters({ ...filters, user_type: e.target.value }); }}
              >
                <option value="">All Types</option>
                <option value="qurator">Qurator</option>
                <option value="qure">Qure</option>
              </select>
              <select
                className="form-select"
                style={{ maxWidth: '220px' }}
                value={filters.org_code}
                onChange={(e) => { setFilters({ ...filters, org_code: e.target.value }); }}
              >
                <option value="">All Organizations</option>
                {organizations.map(org => (
                  <option key={org.org_id} value={org.org_code}>{org.org_name} ({org.org_code})</option>
                ))}
              </select>
              <button
                className="adm-btn adm-btn-outline-dark adm-btn-sm"
                data-tooltip="Clear all filters"
                onClick={() => { 
                  setFilters({ user_type: '', org_code: '', search: '' }); 
                }}
              >
                <i className="fas fa-times-circle"></i> Clear Filters
              </button>
            </div>

            {/* Bulk Actions */}
            <div className="adm-bulk">
              <span className="adm-bulk-label"><i className="fas fa-tasks" style={{ color: '#FF5722' }}></i> Bulk Actions:</span>
              <select id="bulkOrgSelect" className="form-select" style={{ maxWidth: '220px' }}>
                <option value="">Select Organization...</option>
                {organizations.map(org => (
                  <option key={org.org_id} value={org.org_code}>{org.org_name} ({org.org_code})</option>
                ))}
              </select>
              <button className="adm-btn adm-btn-warning adm-btn-sm" data-tooltip="Deactivate all users in selected org" onClick={() => bulkAction('deactivate_org')}>
                <i className="fas fa-user-slash"></i> Deactivate Org Users
              </button>
              <button className="adm-btn adm-btn-success adm-btn-sm" data-tooltip="Activate all users in selected org" onClick={() => bulkAction('activate_org')}>
                <i className="fas fa-user-check"></i> Activate Org Users
              </button>
            </div>

            {/* Loading */}
            {loadingUsers && (
              <div className="adm-loading">
                <div className="adm-spinner"></div>
                <span>Loading users...</span>
              </div>
            )}

            {/* Table */}
            {!loadingUsers && (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Organization</th>
                      <th>Type</th>
                      <th>Password</th>
                      <th>PIN</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="10" style={{ textAlign: 'center', padding: '2.5rem', color: '#aaa' }}>
                          <i className="fas fa-users" style={{ fontSize: '2rem', marginBottom: '0.5rem', display: 'block' }}></i>
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map(u => (
                        <tr key={u.user_id} className={u.status === 'inactive' ? 'adm-row-inactive' : ''}>
                          <td><strong style={{ color: '#FF5722' }}>#{u.user_id}</strong></td>
                          <td><strong>{escapeHtml(u.first_name)} {escapeHtml(u.last_name)}</strong></td>
                          <td style={{ color: '#666', fontSize: '0.95rem' }}>{escapeHtml(u.username)}</td>
                          <td style={{ color: '#666', fontSize: '0.9rem' }}>{escapeHtml(u.email)}</td>
                          <td>
                            <span className="adm-badge adm-badge-code">{u.user_code || 'N/A'}</span>
                            <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '2px' }}>{escapeHtml(u.org_name || '')}</div>
                          </td>
                          <td>
                            <span className={`adm-badge ${u.user_type === 'qurator' ? 'adm-badge-qurator' : 'adm-badge-qure'}`}>
                              {u.user_type}
                            </span>
                          </td>
                          <td><span className="adm-pass">{escapeHtml(u.plain_password || '—')}</span></td>
                          <td><span className="adm-pass">{escapeHtml(u.pin || '—')}</span></td>
                          <td>
                            <span className={`adm-badge ${u.status === 'active' ? 'adm-badge-active' : 'adm-badge-inactive'}`}>
                              {u.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button className="adm-action-btn adm-action-btn-copy" onClick={() => copyUserDetails(u)} data-tooltip="Copy User Details">
                              <i className="fas fa-copy"></i>
                            </button>
                            <button className="adm-action-btn adm-action-btn-edit" onClick={() => editUser(u.user_id)} data-tooltip="Edit User">
                              <i className="fas fa-user-edit"></i>
                            </button>
                            {u.status === 'active' ? (
                              <button className="adm-action-btn adm-action-btn-deactivate"
                                onClick={() => showConfirm('toggle_user_status', { user_id: u.user_id, status: 'inactive' }, `Deactivate "${u.first_name} ${u.last_name}"?`, 'warning', false)}
                                data-tooltip="Deactivate User">
                                <i className="fas fa-user-slash"></i>
                              </button>
                            ) : (
                              <button className="adm-action-btn adm-action-btn-activate"
                                onClick={() => showConfirm('toggle_user_status', { user_id: u.user_id, status: 'active' }, `Activate "${u.first_name} ${u.last_name}"?`, 'success', false)}
                                data-tooltip="Activate User">
                                <i className="fas fa-user-check"></i>
                              </button>
                            )}
                            <button className="adm-action-btn adm-action-btn-delete"
                              onClick={() => showConfirm('delete_user', { user_id: u.user_id }, `Permanently delete "${u.first_name} ${u.last_name}"?`, 'danger', true)}
                              data-tooltip="Delete Permanently">
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="adm-pagination">
                <button className="adm-page-btn" data-tooltip="Previous page" onClick={() => loadUsers(currentPage - 1)} disabled={currentPage === 1}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
                    return (
                      <button key={page} className={`adm-page-btn ${page === currentPage ? 'active' : ''}`} data-tooltip={`Go to page ${page}`} onClick={() => loadUsers(page)}>
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} style={{ padding: '0 4px', color: '#aaa', alignSelf: 'center' }}>…</span>;
                  }
                  return null;
                })}
                <button className="adm-page-btn" data-tooltip="Next page" onClick={() => loadUsers(currentPage + 1)} disabled={currentPage === totalPages}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Organization List Modal */}
      {showOrgListModal && (
        <div className="adm-modal-overlay" onClick={() => setShowOrgListModal(false)}>
          <div className="adm-modal adm-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div className="adm-modal-title"><i className="fas fa-building"></i> All Organizations</div>
              <button className="adm-modal-close" onClick={() => setShowOrgListModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="adm-modal-body">
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead><tr><th>ID</th><th>Name</th><th>Code</th><th>Users (Q/R)</th><th>Limits (Q/R)</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {allOrganizations.map(org => (
                      <tr key={org.org_id}>
                        <td><strong style={{ color: '#FF5722' }}>#{org.org_id}</strong></td>
                        <td><strong>{escapeHtml(org.org_name)}</strong></td>
                        <td><span className="adm-badge adm-badge-code">{escapeHtml(org.org_code)}</span></td>
                        <td>{org.current_qurators} / {org.current_qures}</td>
                        <td>{org.number_of_qurator} / {org.number_of_qure}</td>
                        <td><span className={`adm-badge ${org.status === 'active' ? 'adm-badge-active' : 'adm-badge-inactive'}`}>{org.status}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="adm-action-btn adm-action-btn-edit" onClick={() => { setShowOrgListModal(false); editOrganization(org.org_id); }} data-tooltip="Edit Organization"><i className="fas fa-pen"></i></button>
                          {org.status === 'active'
                            ? <button className="adm-action-btn adm-action-btn-deactivate" onClick={() => showConfirm('toggle_org_status', { org_id: org.org_id, status: 'inactive' }, `Deactivate "${org.org_name}"?`, 'warning', false)} data-tooltip="Deactivate"><i className="fas fa-toggle-off"></i></button>
                            : <button className="adm-action-btn adm-action-btn-activate" onClick={() => showConfirm('toggle_org_status', { org_id: org.org_id, status: 'active' }, `Activate "${org.org_name}"?`, 'success', false)} data-tooltip="Activate"><i className="fas fa-toggle-on"></i></button>
                          }
                          <button className="adm-action-btn adm-action-btn-delete" onClick={() => showConfirm('delete_org', { org_id: org.org_id }, `Delete organization "${org.org_name}" and ALL its users?`, 'danger', true)} data-tooltip="Delete Permanently"><i className="fas fa-trash-alt"></i></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-dark" data-tooltip="Close this modal" onClick={() => setShowOrgListModal(false)}>
                <i className="fas fa-times"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <div className="adm-modal-overlay" onClick={() => { setShowCreateOrgModal(false); resetOrgForm(); }}>
          <div className="adm-modal adm-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div className="adm-modal-title"><i className="fas fa-building"></i> Create Organization</div>
              <button className="adm-modal-close" onClick={() => { setShowCreateOrgModal(false); resetOrgForm(); }}><i className="fas fa-times"></i></button>
            </div>
              <form onSubmit={handleOrgSubmit}>
              <div className="adm-modal-body">
                <div className="mb-3"><label className="form-label">Organization Name</label>
                  <input type="text" className="form-control" required value={orgForm.org_name} onChange={(e) => setOrgForm({ ...orgForm, org_name: e.target.value })} placeholder="e.g. Northeastern University" /></div>
                <div className="mb-3"><label className="form-label">Organization Code</label>
                  <input type="text" className="form-control" maxLength="10" required value={orgForm.org_code} onChange={(e) => setOrgForm({ ...orgForm, org_code: e.target.value })} placeholder="e.g. NEU" />
                  <div className="form-text">Unique code, max 10 characters</div></div>
                <div className="row">
                  <div className="col-6 mb-2"><label className="form-label">Max Qurators</label>
                    <input type="number" className="form-control" min="1" required value={orgForm.number_of_qurator} onChange={(e) => setOrgForm({ ...orgForm, number_of_qurator: parseInt(e.target.value) })} /></div>
                  <div className="col-6 mb-2"><label className="form-label">Max Qures</label>
                    <input type="number" className="form-control" min="1" required value={orgForm.number_of_qure} onChange={(e) => setOrgForm({ ...orgForm, number_of_qure: parseInt(e.target.value) })} /></div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-dark" data-tooltip="Cancel and close" onClick={() => { setShowCreateOrgModal(false); resetOrgForm(); }}>
                  <i className="fas fa-times"></i> Cancel
                </button>
                <button type="submit" className="adm-btn adm-btn-primary" data-tooltip="Create new organization">
                  <i className="fas fa-save"></i> Create
                </button>
              </div>
              </form>
          </div>
        </div>
      )}

      {/* Edit Organization Modal */}
      {showEditOrgModal && (
        <div className="adm-modal-overlay" onClick={() => { setShowEditOrgModal(false); resetOrgForm(); }}>
          <div className="adm-modal adm-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div className="adm-modal-title"><i className="fas fa-pen"></i> Edit Organization</div>
              <button className="adm-modal-close" onClick={() => { setShowEditOrgModal(false); resetOrgForm(); }}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleOrgSubmit}>
              <div className="adm-modal-body">
                <input type="hidden" value={orgForm.org_id} />
                <div className="mb-3"><label className="form-label">Organization Name</label>
                  <input type="text" className="form-control" required value={orgForm.org_name} onChange={(e) => setOrgForm({ ...orgForm, org_name: e.target.value })} /></div>
                <div className="mb-3"><label className="form-label">Organization Code</label>
                  <input type="text" className="form-control" disabled value={orgForm.org_code} style={{ background: '#f8f9fa' }} />
                  <div className="form-text">Code cannot be changed</div></div>
                <div className="row">
                  <div className="col-6 mb-2"><label className="form-label">Max Qurators</label>
                    <input type="number" className="form-control" min="1" required value={orgForm.number_of_qurator} onChange={(e) => setOrgForm({ ...orgForm, number_of_qurator: parseInt(e.target.value) })} /></div>
                  <div className="col-6 mb-2"><label className="form-label">Max Qures</label>
                    <input type="number" className="form-control" min="1" required value={orgForm.number_of_qure} onChange={(e) => setOrgForm({ ...orgForm, number_of_qure: parseInt(e.target.value) })} /></div>
                </div>
              </div>
              <div className="adm-modal-footer">
                <button type="button" className="adm-btn adm-btn-dark" data-tooltip="Cancel and close" onClick={() => { setShowEditOrgModal(false); resetOrgForm(); }}>
                  <i className="fas fa-times"></i> Cancel
                </button>
                <button type="submit" className="adm-btn adm-btn-warning" data-tooltip="Save changes">
                  <i className="fas fa-save"></i> Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="adm-modal-overlay" onClick={() => { setShowCreateUserModal(false); resetUserForm(); }}>
          <div className="adm-modal adm-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div className="adm-modal-title"><i className="fas fa-user-plus"></i> Create User</div>
              <button className="adm-modal-close" onClick={() => { setShowCreateUserModal(false); resetUserForm(); }}><i className="fas fa-times"></i></button>
            </div>
              <form onSubmit={handleUserSubmit}>
                <div className="adm-modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">First Name</label>
                      <input type="text" className="form-control" required value={userForm.first_name} onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Last Name</label>
                      <input type="text" className="form-control" required value={userForm.last_name} onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Username</label>
                      <input type="text" className="form-control" required value={userForm.username} onChange={(e) => setUserForm({ ...userForm, username: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" required value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">User Type</label>
                      <select className="form-select" required value={userForm.user_type} onChange={(e) => setUserForm({ ...userForm, user_type: e.target.value })}>
                        <option value="qurator">Qurator</option>
                        <option value="qure">Qure</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Organization</label>
                      <select className="form-select" required value={userForm.user_code} onChange={(e) => setUserForm({ ...userForm, user_code: e.target.value })}>
                        <option value="">Select Organization</option>
                        {organizations.map(org => (
                          <option key={org.org_id} value={org.org_code}>
                            {org.org_name} ({org.org_code}) - Q:{org.remaining_qurators}, R:{org.remaining_qures}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">PIN</label>
                      <input type="text" className="form-control" maxLength="10" required value={userForm.pin} onChange={(e) => setUserForm({ ...userForm, pin: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="form-check mt-4">
                        <input className="form-check-input" type="checkbox" checked={userForm.auto_generate_password}
                          onChange={(e) => setUserForm({ ...userForm, auto_generate_password: e.target.checked, password: '' })} />
                        <label className="form-check-label">Auto-generate Password</label>
                      </div>
                    </div>
                  </div>
                  {!userForm.auto_generate_password && (
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <input type="password" className="form-control" required value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
                      <div className="form-text">Required when not auto-generating</div>
                    </div>
                  )}
                </div>
                <div className="adm-modal-footer">
                  <button type="button" className="adm-btn adm-btn-dark" data-tooltip="Cancel and close" onClick={() => { setShowCreateUserModal(false); resetUserForm(); }}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="adm-btn adm-btn-success" data-tooltip="Create new user">
                    <i className="fas fa-user-plus"></i> Create User
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="adm-modal-overlay" onClick={() => { setShowEditUserModal(false); resetUserForm(); }}>
          <div className="adm-modal adm-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header">
              <div className="adm-modal-title"><i className="fas fa-user-edit"></i> Edit User</div>
              <button className="adm-modal-close" onClick={() => { setShowEditUserModal(false); resetUserForm(); }}><i className="fas fa-times"></i></button>
            </div>
              <form onSubmit={handleUserSubmit}>
                <div className="adm-modal-body">
                  <input type="hidden" value={userForm.user_id} />
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        value={userForm.first_name}
                        onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        value={userForm.last_name}
                        onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        required
                        value={userForm.username}
                        onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        required
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">User Type</label>
                      <select
                        className="form-select"
                        required
                        value={userForm.user_type}
                        onChange={(e) => setUserForm({ ...userForm, user_type: e.target.value })}
                      >
                        <option value="qurator">Qurator</option>
                        <option value="qure">Qure</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Organization</label>
                      <input
                        type="text"
                        className="form-control"
                        disabled
                        value={userForm.user_code}
                      />
                      <div className="form-text">Organization cannot be changed</div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">PIN</label>
                      <input
                        type="text"
                        className="form-control"
                        maxLength="10"
                        required
                        value={userForm.pin}
                        onChange={(e) => setUserForm({ ...userForm, pin: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">New Password (Optional)</label>
                      <input
                        type="password"
                        className="form-control"
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      />
                      <div className="form-text">Leave blank to keep current password</div>
                    </div>
                  </div>
                </div>
                <div className="adm-modal-footer">
                  <button type="button" className="adm-btn adm-btn-dark" data-tooltip="Cancel and close" onClick={() => { setShowEditUserModal(false); resetUserForm(); }}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="adm-btn adm-btn-warning" data-tooltip="Save user changes">
                    <i className="fas fa-save"></i> Update User
                  </button>
                </div>
              </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmAction && (
        <div className="adm-modal-overlay" onClick={() => { setShowConfirmModal(false); setConfirmAction(null); }}>
          <div className="adm-modal adm-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="adm-modal-header" style={{ background: confirmAction.type === 'danger' ? '#e74c3c' : confirmAction.type === 'warning' ? '#e67e22' : '#27ae60' }}>
              <div className="adm-modal-title">
                <i className={`fas ${confirmAction.type === 'danger' ? 'fa-exclamation-triangle' : confirmAction.type === 'warning' ? 'fa-exclamation-circle' : 'fa-question-circle'}`}></i> Confirm Action
              </div>
              <button className="adm-modal-close" onClick={() => { setShowConfirmModal(false); setConfirmAction(null); }}><i className="fas fa-times"></i></button>
            </div>
            <div className="adm-modal-body">
              <p style={{ fontSize: '0.95rem', color: '#444', marginBottom: confirmAction.showWarning ? '1rem' : 0 }}>{confirmAction.message}</p>
              {confirmAction.showWarning && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '0.75rem 1rem', color: '#856404', fontSize: '0.875rem' }}>
                  <i className="fas fa-exclamation-triangle me-2"></i> This action <strong>cannot be undone!</strong>
                </div>
              )}
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-btn adm-btn-dark" data-tooltip="Cancel this action" onClick={() => { setShowConfirmModal(false); setConfirmAction(null); }}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button type="button"
                className={`adm-btn ${confirmAction.type === 'danger' ? 'adm-btn-danger' : confirmAction.type === 'warning' ? 'adm-btn-warning' : 'adm-btn-success'}`}
                data-tooltip="Confirm and proceed"
                onClick={executeConfirm}>
                <i className="fas fa-check"></i> Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
